<template>
    <slot />
</template>

<script setup>
    import { disposeMaterial } from '#imports';
    import { Color, Mesh, RepeatWrapping } from 'three';
    import { computed, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue';

    import { lerp } from '@resn/gozer-math';
    import { simplePlaneGeometry } from '@resn/gozer-three';
    import { disposeObject } from '@resn/gozer-three/helpers';
    import { usePane, useRafBool, useResize, useThreeObject } from '@resn/gozer-vue';
    import gsap from '@resn/gsap';

    import glslFill from '@/libs/lygia/draw/fill.glsl';
    import glslConst from '@/libs/lygia/math/const.glsl';
    import glslRotate from '@/libs/lygia/space/rotate.glsl';
    import SimplePlane from '~/components/gl/common/SimplePlane.vue';
    import CustomMaterial from '~/components/gl/materials/custom-material';
    import {
        uniforms as matcapUniforms,
        shaderChunks as ShaderChunksMatcap,
    } from '~/components/gl/shaders/ShaderChunks/matcap';
    import { glslSpiralVortex } from '~/components/gl/shaders/ShaderChunks/spiral-vortex';
    import { useModelExporter } from '~/composables/useModelExporter';
    import { LAYER_BG } from '~/core/constants';
    import { createNoiseFromTexture2 } from '~/libs/three/noise-texture';
    import { useGlobalAssets } from '~/providers/GlobalAssets';

    const props = defineProps({
        layer: {
            type: Number,
            default: 0,
        },
        visible: {
            type: Boolean,
            default: false,
        },
    });

    const v = computed(() => props.visible);

    const active = ref(true);
    const progress = ref(1);
    const uSwirlProgress = ref(0.75);
    const uSpinSpeed = ref(0.36);
    const uTwistAmount = ref(15.22);
    const uNoiseScale = ref(0.23);
    const uNoiseTightness = ref(0.29);

    const material = new CustomMaterial({
        uniforms: {
            uTime: 0,
            uProgress: 0,
            uSwirlProgress: 0,
            uTwistAmount: 0,
            uSpinSpeed: 0,
            uNoiseMap: null,
            uNoiseScale: 0,
            uNoiseTightness: 0,

            ...matcapUniforms(),
        },
        vs: /* glsl */ `
            varying vec2 vUv;
            varying vec3 vNormal;
            varying vec3 vMvPosition;

            void main() {
                vec4 mvPosition = modelViewMatrix * vec4( position, 1.0 );
                gl_Position = projectionMatrix * mvPosition;
                
                vUv = uv;
                vMvPosition = -mvPosition.xyz;
                vNormal = normalize(normalMatrix * normal); // view-space normal
            }
        `,
        fs: `
            uniform float uTime;
            uniform float uProgress;
            uniform float uSwirlProgress;
            uniform float uTwistAmount;
            uniform float uSpinSpeed;
            uniform sampler2D uNoiseMap;
            uniform float uNoiseScale;
            uniform float uNoiseTightness;
            
            varying vec2 vUv;
            varying vec3 vNormal;
            varying vec3 vMvPosition;

            ${glslConst}
            ${glslRotate}
            ${glslFill}
            ${glslSpiralVortex}
            ${createNoiseFromTexture2}
            ${ShaderChunksMatcap.funcs}
            ${ShaderChunksMatcap.uniforms}

            void main() {
                vec3 normal = normalize(vNormal);
                float radius = length(vUv - 0.5);

                float spinSpeed = -uTime * uSpinSpeed;
                vec2 noiseUv = vUv;
                float spiralNoise = spiralWarpedNoise(
                    uNoiseMap,
                    noiseUv,
                    uNoiseScale,
                    spinSpeed,
                    uTwistAmount,
                    uNoiseTightness
                );
                
                float sdf = spiralNoise;
                sdf *= mix(0.0, 0.99, smoothstep(0.6, 0.0, radius));
                
                float mask = 1.0 - fill(sdf, mix(0.7, 0.0, uSwirlProgress));
                
                vec3 cMatcap = matcap(tMatcap, normal, vMvPosition, false, 1.0, 1.0, 1.0, u_matcapRotation);
                
                float alpha = mask * uProgress;
                
                gl_FragColor = vec4(cMatcap, alpha);
                // gl_FragColor = vec4(vec3(sdf), 1.0);
                
                #include <tonemapping_fragment>
                #include <colorspace_fragment>
            }
        `,
        options: { name: 'Mega Bg', depthWrite: false },
    });

    const mesh = new Mesh(simplePlaneGeometry, material);

    useThreeObject(mesh, { name: 'Mega Bg', props: { v } });

    mesh.position.z = -0.2;
    mesh.scale.setScalar(2);
    mesh.layers.set(props.layer);
    mesh.renderOrder = -1;

    useGlobalAssets(({ data }) => {
        const { noise1, matcapIrridescent3 } = data;

        material.uNoiseMap = noise1;
        material.tMatcap = matcapIrridescent3;
    });

    let tl = null;
    const show = ({ delay = 0, swirl = true } = {}) => {
        tl?.kill();

        const duration = swirl ? 0.8 : 0.5;
        const ease = 'power2.out';

        tl = gsap.timeline({ delay });
        tl.fromTo(uSwirlProgress, { value: swirl ? 0 : 1 }, { value: 1, duration, ease }, 0);
        tl.fromTo(progress, { value: swirl ? 1 : 0 }, { value: 1, duration, ease }, 0);
    };

    const hide = ({ delay = 0, swirl = true } = {}) => {
        tl?.kill();

        const duration = swirl ? 0.5 : 0.5;
        const ease = 'power2.in';

        tl = gsap.timeline({ delay });
        tl.to(uSwirlProgress, { value: swirl ? 0 : 1, duration, ease }, 0);
        tl.to(progress, { value: swirl ? 1 : 0, duration, ease }, 0);
    };

    watchEffect(() => {
        material.uniforms.uProgress.value = progress.value;
        material.uniforms.uSwirlProgress.value = uSwirlProgress.value;
        material.uniforms.uTwistAmount.value = uTwistAmount.value;
        material.uniforms.uSpinSpeed.value = uSpinSpeed.value;
        material.uniforms.uNoiseScale.value = uNoiseScale.value;
        material.uniforms.uNoiseTightness.value = uNoiseTightness.value;
    });

    watch(active, (val) => {
        if (val) show();
        else hide();
    });

    useRafBool(active, ({ timestamp }) => {
        material.uniforms.uTime.value = timestamp * 0.001;
    });

    const destroy = () => {
        disposeMaterial(material);
    };

    onBeforeUnmount(destroy);

    defineExpose({ show, hide });

    const paneOptions = {
        min: 0,
        max: 1,
        step: 0.01,
    };

    const modelExport = useModelExporter();

    const showPane = false;
    if (!modelExport && showPane) {
        usePane(
            [
                { name: 'progress', value: progress, options: paneOptions },
                { name: 'uSwirlProgress', value: uSwirlProgress, options: paneOptions },
                {
                    name: 'uTwistAmount',
                    value: uTwistAmount,
                    options: { min: 0, max: 20, step: 0.01 },
                },
                { name: 'uSpinSpeed', value: uSpinSpeed, options: { min: 0, max: 3, step: 0.01 } },
                {
                    name: 'uNoiseScale',
                    value: uNoiseScale,
                    options: { min: 0, max: 3, step: 0.01 },
                },
                { name: 'uNoiseTightness', value: uNoiseTightness, options: paneOptions },
                { name: 'active', value: active },
            ],
            { title: 'Mega Bg', expanded: false }
        );
    }
</script>
